import React, { useState, useRef } from 'react'
import PropTypes from 'prop-types';
import ReactQuill, { Quill } from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import baseUrl from '../../../../config/baseUrl'
import 'react-pdf/dist/esm/Page/AnnotationLayer.css'
import { marked } from 'marked'
import {
  Alert,
  Grid,
  Snackbar,
  Box,
  Button,
  CircularProgress,
  useMediaQuery,
  Radio,
  RadioGroup,
  FormControlLabel,
  // MenuItem
  // TextField,
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop'

// Create custom Blot for table support as an embed
const BlockEmbed = Quill.import('blots/block/embed')

class TableBlot extends BlockEmbed {
  static create(value) {
    const node = super.create(value)
    if (typeof value === 'string') {
      // If value is HTML string, create a div wrapper and insert the table HTML
      const wrapper = document.createElement('div')
      wrapper.innerHTML = value
      const table = wrapper.querySelector('table')
      if (table) {
        return table
      }
      node.innerHTML = value
    }
    return node
  }

  static value(node) {
    return node.outerHTML
  }
}
TableBlot.blotName = 'table'
TableBlot.tagName = 'TABLE'
TableBlot.className = 'ql-table'

// Define font families and sizes outside component
const fontFamilyArr = [
  'Tiro Devanagari Hindi',
  'Roboto Condensed',
  'Times New Roman',
  'Calibri',
  'Calibri Light',
  'Sans-Serif',
]

const fontSizeArr = ['10px', '11px', '12px', '14px', '18px', '24px']

// Custom Clipboard module
const Clipboard = Quill.import('modules/clipboard')

class CustomClipboard extends Clipboard {
  convert(html) {
    // Check if HTML contains tables
    if (html && (html.includes('<table') || html.includes('<TABLE'))) {
      // Extract tables and handle them separately
      const wrapper = document.createElement('div')
      wrapper.innerHTML = html
      const tables = wrapper.querySelectorAll('table')

      // For each table, preserve it as-is
      tables.forEach(table => {
        // Make sure table has proper structure
        if (!table.querySelector('tbody')) {
          const tbody = document.createElement('tbody')
          while (table.firstChild) {
            tbody.appendChild(table.firstChild)
          }
          table.appendChild(tbody)
        }
      })

      html = wrapper.innerHTML
    }

    return super.convert(html)
  }

  onPaste(e) {
    e.preventDefault()
    const clipboardData = e.clipboardData || window.clipboardData
    const pastedHtml = clipboardData.getData('text/html') || clipboardData.getData('text/plain')

    if (pastedHtml) {
      const quill = this.quill
      const cursorPosition = quill.getSelection()?.index || 0 // Get current cursor position
      const processedHtml = marked(pastedHtml)
      quill.clipboard.dangerouslyPasteHTML(cursorPosition, processedHtml) // Insert content at cursor
    }
  }
}

// Register custom modules only once (outside component)
let isQuillConfigured = false
if (!isQuillConfigured) {
  // Register table format
  Quill.register({
    'formats/table': TableBlot,
  }, true)

  // Register custom clipboard
  Quill.register('modules/clipboard', CustomClipboard, true)

  // Register fonts
  const Font = Quill.import('attributors/style/font')
  Font.whitelist = fontFamilyArr
  Quill.register(Font, true)

  // Register sizes
  const Size = Quill.import('attributors/style/size')
  Size.whitelist = fontSizeArr
  Quill.register(Size, true)

  isQuillConfigured = true
}

const EditablePdfReader = (props) => {

  const { ans } = props
  const quillRef = useRef(null)
  const [file, setFile] = useState(null)

  const [editorHtml, setEditorHtml] = useState('')
  const isProgrammaticUpdate = useRef(false) // Flag to track programmatic updates

  const [state, setState] = React.useState({ value: null })

  // Snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [snackbarSeverity, setSnackbarSeverity] = useState('success')

  // const showSnackbar = useSnackbar()

  // Loading state
  const [isLoading, setIsLoading] = useState(false)

  const uId = sessionStorage.getItem('userId')

  const [userId, setUserId] = useState('')

  const isSmallScreen = useMediaQuery('(max-width: 600px)')

  const [selectedOption, setSelectedOption] = useState('pdf')
  const [isDownloadPressed, setIsDownloadPressed] = useState(false)

  const handleEditorChange = (content, delta, source, editor) => {
    setEditorHtml(editor.getHTML()) // Get HTML from the editor

  }

  // Effect for mount-only logic (event listeners)
  React.useEffect(() => {
    setFile(null)

    const handleBeforeUnload = (event) => {
      // event.preventDefault()
      // sessionStorage.setItem('editorHtml', editorHtml)
      // event.returnValue = '' // Required for the prompt to show
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    window.addEventListener('afterUnload', handleAfterUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('afterUnload', handleAfterUnload)
    }
  }, [])

  // Effect to format and display content when ans prop changes
  React.useEffect(() => {
    if (ans) {
      formatIncomingAns()
      setState({ ...state, value: ans })
    }
  }, [ans])

  // Update editor content when editorHtml changes (only for programmatic updates)
  React.useEffect(() => {
    // Only update if this is a programmatic update (not user typing)
    if (quillRef.current && editorHtml && isProgrammaticUpdate.current) {
      const quill = quillRef.current.getEditor()
      const currentContent = quill.root.innerHTML
      // Only update if content is different to avoid unnecessary re-renders
      const normalizedCurrent = currentContent.replace(/\s+/g, ' ').trim()
      const normalizedNew = editorHtml.replace(/\s+/g, ' ').trim()

      if (normalizedCurrent !== normalizedNew) {
        // Clear the editor first
        quill.setText('')
        // Insert the HTML at the beginning
        setTimeout(() => {
          quill.clipboard.dangerouslyPasteHTML(0, editorHtml)
          // Reset the flag after update
          isProgrammaticUpdate.current = false
        }, 0)
      } else {
        // Reset flag even if no update needed
        isProgrammaticUpdate.current = false
      }
    }
  }, [editorHtml])

  const handleAfterUnload = (event) => {
    sessionStorage.setItem('editorHtml', editorHtml)
    event.returnValue = '' // Required for the prompt to show
  }

  React.useEffect(() => {
    setUserId(uId)
  }, [uId])

  React.useEffect(() => {
    if (file === 'testing') {
      file && uploadFile()
    }
  }, [file])

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false)
  }

  const formatIncomingAns = () => {
    if (ans) {
      // Configure marked to support GFM (GitHub Flavored Markdown) tables
      marked.setOptions({
        gfm: true,
        breaks: true,
      })

      // Replace escaped newlines with actual newlines
      const unescapedAns = ans.replace(/\\n/g, '\n')
      const html = marked(unescapedAns) // Convert markdown to HTML using marked

      // Set flag to indicate this is a programmatic update
      isProgrammaticUpdate.current = true
      setEditorHtml(html)
    } // return ans
  }

  // const handleChange = (value) => {
  //   setState({ ...state, value: value })
  // }



  const handlePrint = () => {
    // Open window off-screen so only the print dialog is visible
    const printWindow = window.open('', '_blank', 'width=900,height=700,left=-2000,top=-2000')

    // Check if window was blocked by popup blocker
    if (!printWindow) {
      setSnackbarMessage('Please allow popups for this site to print')
      setSnackbarSeverity('warning')
      setSnackbarOpen(true)
      return
    }

    // Write the content to the new window
    printWindow.document.write(`
      <html>
        <head>
          <title>Lawttorney</title>
          <style>
            body { font-family: Arial, sans-serif; }
            table {
              border-collapse: collapse;
              width: 100%;
              margin-top: 1rem;
              margin-bottom: 1rem;
              border: 1px solid #ddd;
            }
            th {
              background-color: #f5f5f5;
              border: 1px solid #ddd;
              padding: 12px;
              text-align: left;
              font-weight: bold;
            }
            td {
              border: 1px solid #ddd;
              padding: 12px;
              text-align: left;
            }
            tr:nth-of-type(even) {
              background-color: #f9f9f9;
            }
          </style>
        </head>
        <body>
          ${editorHtml}
        </body>
      </html>
    `)
    printWindow.document.close()

    // Wait for content to load, then trigger print
    setTimeout(() => {
      try {
        printWindow.print()

        let windowClosed = false

        // Try to close window after print dialog is dismissed
        printWindow.onafterprint = () => {
          if (!windowClosed) {
            windowClosed = true
            setTimeout(() => printWindow.close(), 100)
          }
        }

        // Fallback: Close window after 1 second if onafterprint doesn't fire
        setTimeout(() => {
          if (!windowClosed && !printWindow.closed) {
            printWindow.close()
          }
        }, 1000)

      } catch (error) {
        console.error('Print error:', error)
        setSnackbarMessage('Failed to open print dialog')
        setSnackbarSeverity('error')
        setSnackbarOpen(true)
        printWindow.close()
      }
    }, 250)
  }

  const handleSave = () => {
    if (selectedOption === 'pdf') {
      handleDownloadPDF()
    } else {
      handleDownloadDocx()
    }
    setIsDownloadPressed(false)
  }

  const handleDownloadPDF = () => {
    // Open window off-screen so only the print dialog is visible
    // Keep normal size for proper print rendering
    const printWindow = window.open('', '_blank', 'width=900,height=700,left=-2000,top=-2000');

    // Check if window was blocked by popup blocker
    if (!printWindow) {
      setSnackbarMessage('Please allow popups for this site to download PDF')
      setSnackbarSeverity('warning')
      setSnackbarOpen(true)
      return
    }

    // Write the content to the new window with proper styling
    printWindow.document.open();
    printWindow.document.write(`
    <html>
      <head>
        <title>Lawttorney Document</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 20px;
            line-height: 1.6;
          }
          table {
            border-collapse: collapse;
            width: 100%;
            margin-top: 1rem;
            margin-bottom: 1rem;
            border: 1px solid #ddd;
          }
          th {
            background-color: #f5f5f5;
            border: 1px solid #ddd;
            padding: 12px;
            text-align: left;
            font-weight: bold;
          }
          td {
            border: 1px solid #ddd;
            padding: 12px;
            text-align: left;
          }
          tr:nth-of-type(even) {
            background-color: #f9f9f9;
          }
          @media print {
            /* Ensure proper margins when printing/saving as PDF */
            @page { margin: 1cm; }
          }
        </style>
      </head>
      <body>
        ${editorHtml}
      </body>
    </html>
  `);
    printWindow.document.close();

    // Wait for content to load, then trigger print
    setTimeout(() => {
      try {
        printWindow.print();

        let windowClosed = false;

        // Try to close window after print dialog is dismissed
        printWindow.onafterprint = () => {
          if (!windowClosed) {
            windowClosed = true;
            setTimeout(() => printWindow.close(), 100);
          }
        };

        // Fallback: Close window after 3 seconds if onafterprint doesn't fire
        // (some browsers don't fire onafterprint when saving as PDF)
        setTimeout(() => {
          if (!windowClosed && !printWindow.closed) {
            printWindow.close();
          }
        }, 1000);

      } catch (error) {
        console.error('Print error:', error);
        setSnackbarMessage('Failed to open print dialog')
        setSnackbarSeverity('error')
        setSnackbarOpen(true)
        printWindow.close();
      }
    }, 250);
  };

  const handleDownloadDocx = async () => {

    // Create a Blob containing the HTML content with proper styling
    const htmlContent = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office"
          xmlns:w="urn:schemas-microsoft-com:office:word">
      <head>
        <title>Lawttorney Document</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 20px;
            line-height: 1.6;
          }
          table {
            border-collapse: collapse;
            width: 100%;
            margin-top: 1rem;
            margin-bottom: 1rem;
            border: 1px solid #ddd;
          }
          th {
            background-color: #f5f5f5;
            border: 1px solid #ddd;
            padding: 12px;
            text-align: left;
            font-weight: bold;
          }
          td {
            border: 1px solid #ddd;
            padding: 12px;
            text-align: left;
          }
          tr:nth-of-type(even) {
            background-color: #f9f9f9;
          }
        </style>
        <!--[if gte mso 9]>
          <xml>
            <w:WordDocument>
              <w:View>Print</w:View>
              <w:Zoom>100</w:Zoom>
            </w:WordDocument>
          </xml>
        <![endif]-->
      </head>
      <body>
        ${editorHtml}
      </body>
    </html>
  `;

    // Convert HTML to a Blob
    const blob = new Blob(
      ['\ufeff', htmlContent], // UTF-8 BOM for proper encoding
      { type: 'application/msword' } // MIME type for Word
    );

    // Create a download link and trigger it
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'lawttorney-document.doc'; // .doc or .docx
    document.body.appendChild(a);
    a.click();

    // Cleanup
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
  };


  const handleOptionChange = (event) => {
    setSelectedOption(event.target.value)
  }

  const uploadFile = async () => {
    setIsLoading(true)
    const formData = new FormData()
    formData.append('userid', userId)
    formData.append('editordocx', file)
    try {
      const response = await fetch(`${baseUrl}/api/editdocx/editdocxupload`, {
        method: 'POST',
        body: formData,
      })

      if (!response.ok) {
        setSnackbarMessage('Oops something went wrong while uploading file...')
        setSnackbarSeverity('error')
        setSnackbarOpen(true)

        throw new Error('Network response was not ok')
      }

      const data = await response.json()

      if (data?.status) {
        setSnackbarMessage('File uploaded successfully')
        setSnackbarSeverity('success')
        setSnackbarOpen(true)
        readUploadedFile(data)
        setFile(null)
      }
    } catch (error) {
      setIsLoading(false)
      console.error('Error uploading file:', error)
    }
  }

  const readUploadedFile = async (data) => {
    // console.log('readUploadedFile data', data)
    try {
      const getReadFileResponse = await fetch(
        `${baseUrl}/api/editdocx/readEditDocx/${data.data.id}`,
      )
      if (!getReadFileResponse.ok) {
        setSnackbarMessage('Oops something went wrong while reading file...')
        setSnackbarSeverity('error')
        setSnackbarOpen(true)
        throw new Error('something went wrong while reading file...')
      }

      const getReadFileData = await getReadFileResponse.json()
      // console.log('getReadFileData', getReadFileData)

      if (getReadFileData?.status) {
        getReadFileData && setEditorHtml(getReadFileData?.data)
        setIsLoading(false)
      } else {
        setIsLoading(false)
        throw new Error('Something went wrong while reading file...')
      }
    } catch (error) {
      setSnackbarMessage(error)
      setSnackbarSeverity('error')
      setSnackbarOpen(true)
      setIsLoading(false)
    }
  }

  EditablePdfReader.propTypes = {
    qs: PropTypes.string.isRequired,
    ans: PropTypes.string.isRequired,
    hId: PropTypes.string.isRequired,
  }

  return (
    <>
      <style>
        {`
          /* Default line spacing for editor content */
          .ql-editor,
          .ql-editor * {
            line-height: 1.5 !important;
          }
          .ql-editor p {
            line-height: 1.5 !important;
            margin-bottom: 0 !important;
            margin-top: 0 !important;
          }
          .ql-editor ol,
          .ql-editor ul {
            line-height: 1.5 !important;
            padding-left: 1.5em !important;
          }
          .ql-editor li {
            line-height: 1.5 !important;
          }
          .ql-editor pre,
          .ql-editor blockquote,
          .ql-editor h1,
          .ql-editor h2,
          .ql-editor h3,
          .ql-editor h4,
          .ql-editor h5,
          .ql-editor h6 {
            line-height: 1.5 !important;
          }

          /* Font dropdown width */
          .ql-snow .ql-picker.ql-font {
            width: 180px !important;
          }
          .ql-snow .ql-picker.ql-font .ql-picker-options {
            width: 180px !important;
          }
          .ql-snow .ql-picker.ql-font .ql-picker-label {
            width: 100% !important;
          }

          /* Font dropdown labels */
          .ql-snow .ql-picker.ql-font .ql-picker-label[data-value='Tiro Devanagari Hindi']::before,
          .ql-snow .ql-picker.ql-font .ql-picker-item[data-value='Tiro Devanagari Hindi']::before {
            content: 'Tiro Devanagari Hindi' !important;
          }
          .ql-snow .ql-picker.ql-font .ql-picker-label[data-value='Roboto Condensed']::before,
          .ql-snow .ql-picker.ql-font .ql-picker-item[data-value='Roboto Condensed']::before {
            content: 'Roboto Condensed' !important;
          }
          .ql-snow .ql-picker.ql-font .ql-picker-label[data-value='Times New Roman']::before,
          .ql-snow .ql-picker.ql-font .ql-picker-item[data-value='Times New Roman']::before {
            content: 'Times New Roman' !important;
          }
          .ql-snow .ql-picker.ql-font .ql-picker-label[data-value='Calibri']::before,
          .ql-snow .ql-picker.ql-font .ql-picker-item[data-value='Calibri']::before {
            content: 'Calibri' !important;
          }
          .ql-snow .ql-picker.ql-font .ql-picker-label[data-value='Calibri Light']::before,
          .ql-snow .ql-picker.ql-font .ql-picker-item[data-value='Calibri Light']::before {
            content: 'Calibri Light' !important;
          }
          .ql-snow .ql-picker.ql-font .ql-picker-label[data-value='Sans-Serif']::before,
          .ql-snow .ql-picker.ql-font .ql-picker-item[data-value='Sans-Serif']::before {
            content: 'Sans-Serif' !important;
          }

          /* Size dropdown labels */
          .ql-snow .ql-picker.ql-size .ql-picker-label[data-value='10px']::before,
          .ql-snow .ql-picker.ql-size .ql-picker-item[data-value='10px']::before {
            content: '10px' !important;
          }
          .ql-snow .ql-picker.ql-size .ql-picker-label[data-value='11px']::before,
          .ql-snow .ql-picker.ql-size .ql-picker-item[data-value='11px']::before {
            content: '11px' !important;
          }
          .ql-snow .ql-picker.ql-size .ql-picker-label[data-value='12px']::before,
          .ql-snow .ql-picker.ql-size .ql-picker-item[data-value='12px']::before {
            content: '12px' !important;
          }
          .ql-snow .ql-picker.ql-size .ql-picker-label[data-value='14px']::before,
          .ql-snow .ql-picker.ql-size .ql-picker-item[data-value='14px']::before {
            content: '14px' !important;
          }
          .ql-snow .ql-picker.ql-size .ql-picker-label[data-value='18px']::before,
          .ql-snow .ql-picker.ql-size .ql-picker-item[data-value='18px']::before {
            content: '18px' !important;
          }
          .ql-snow .ql-picker.ql-size .ql-picker-label[data-value='24px']::before,
          .ql-snow .ql-picker.ql-size .ql-picker-item[data-value='24px']::before {
            content: '24px' !important;
          }

          /* Apply actual font styles to content */
          .ql-font-Tiro-Devanagari-Hindi {
            font-family: 'Tiro Devanagari Hindi';
          }
          .ql-font-Roboto-Condensed {
            font-family: 'Roboto Condensed';
          }
          .ql-font-Times-New-Roman {
            font-family: 'Times New Roman';
          }
          .ql-font-Calibri {
            font-family: 'Calibri';
          }
          .ql-font-Calibri-Light {
            font-family: 'Calibri Light';
          }
          .ql-font-Sans-Serif {
            font-family: 'Sans-Serif';
          }
        `}
      </style>
      <Box height='100%' position='relative'>
        {isLoading && (
          <Box
            sx={{
              position: 'absolute',
              height: 'calc(100% - 4rem)',
              width: '100%',
              top: '50%',
              left: '45%',
              // padding: '10px',
            }}
          >
            <CircularProgress color='success' />
          </Box>
        )}
        <React.Fragment>
          <Box sx={{ height: '100%', width: '100%' }}>
            <Grid container justifyContent='flex-end' marginBottom='10px'>
              <Button // button to upload file
                startIcon={<LocalPrintshopIcon />}
                variant='contained'
                sx={{
                  height: isSmallScreen ? '30px' : '40px',
                  width: isSmallScreen ? '100px' : '140px',
                  marginRight: '10px',
                  marginTop: isSmallScreen ? '0px' : '10px',
                  color: 'black',
                  fontSize: isSmallScreen ? '10px' : '',
                }}
                onClick={handlePrint}
              >
                Print
              </Button>

              {/* Download button to save file in pdf or docs format */}
              <Button // button to upload file
                startIcon={<DownloadIcon />}
                variant='contained'
                sx={{
                  height: isSmallScreen ? '30px' : '40px',
                  width: isSmallScreen ? '100px' : '140px',
                  marginRight: '10px',
                  marginTop: isSmallScreen ? '0px' : '10px',
                  color: 'black',
                  fontSize: isSmallScreen ? '10px' : '',
                }}
                onClick={() => {
                  setIsDownloadPressed(true)
                }}
              >
                Download
              </Button>
            </Grid>

            {isDownloadPressed && (
              <div>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                  <label>Select a file format : </label>
                  <RadioGroup value={selectedOption} onChange={handleOptionChange}>
                    <Box
                      sx={{
                        ml: '.5rem',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <FormControlLabel
                        value='pdf'
                        control={<Radio style={{ color: 'navy' }} />}
                        label='PDF'
                      />
                      <FormControlLabel
                        value='word'
                        control={<Radio style={{ color: 'navy' }} />}
                        label='Word'
                      />
                    </Box>
                  </RadioGroup>
                  <Button
                    variant='contained'
                    color='primary'
                    sx={{
                      height: isSmallScreen ? '30px' : '40px',
                      width: isSmallScreen ? '80px' : '100px',
                      marginRight: '10px',
                      marginLeft: '10px',
                      marginTop: isSmallScreen ? '0px' : '10px',
                      color: 'black',
                      fontSize: isSmallScreen ? '10px' : '',
                    }}
                    onClick={handleSave}
                  >
                    Save
                  </Button>

                  <Button
                    variant='outlined'
                    color='primary'
                    sx={{
                      height: isSmallScreen ? '30px' : '40px',
                      width: isSmallScreen ? '80px' : '90px',
                      marginRight: '10px',
                      marginTop: isSmallScreen ? '0px' : '10px',
                      // color: 'black',
                      fontSize: isSmallScreen ? '10px' : '',
                    }}
                    onClick={() => {
                      setIsDownloadPressed(false)
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              </div>
            )}

            <Box
              sx={{
                '& .my-quill-editor': {
                  display: 'flex',
                  flexDirection: 'column',
                  height: 'calc(100dvh - 8rem)',
                },
                '& .my-quill-editor .ql-toolbar': {
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                  backgroundColor: '#fff',
                  borderBottom: '1px solid #ccc',
                },
                '& .my-quill-editor .ql-container': {
                  flex: 1,
                  overflowY: 'auto',
                  border: 'none',
                },
                '& .ql-editor table': {
                  borderCollapse: 'collapse',
                  width: '100%',
                  marginTop: '1rem',
                  marginBottom: '1rem',
                  border: '1px solid #ddd',
                },
                '& .ql-editor thead th': {
                  backgroundColor: '#f5f5f5',
                  border: '1px solid #ddd',
                  padding: '12px',
                  textAlign: 'left',
                  fontWeight: 'bold',
                },
                '& .ql-editor th': {
                  backgroundColor: '#f5f5f5',
                  border: '1px solid #ddd',
                  padding: '12px',
                  textAlign: 'left',
                  fontWeight: 'bold',
                },
                '& .ql-editor td': {
                  border: '1px solid #ddd',
                  padding: '12px',
                  textAlign: 'left',
                },
                '& .ql-editor tbody tr:nth-of-type(even)': {
                  backgroundColor: '#f9f9f9',
                },
                '& .ql-editor tbody tr:hover': {
                  backgroundColor: '#f0f0f0',
                },
              }}
            >
              {/* <EditorToolbar /> */}
              {/* <ReactQuill
                  theme='snow'
                  style={{ height: '500px', overflowY: 'scroll' }}
                  value={state.value}
                  onChange={handleChange}
                  placeholder={'Write something awesome...'}
                  modules={modules}
                  formats={formats}
                /> */}

              <ReactQuill
                className='my-quill-editor'
                fontSize={12}
                theme='snow'
                placeholder='Data'
                multirows
                value={editorHtml}
                ref={quillRef}
                onChange={handleEditorChange}
                formats={[
                  'header',
                  'font',
                  'size',
                  'bold',
                  'italic',
                  'underline',
                  'strike',
                  'list',
                  'bullet',
                  'align',
                  'link',
                  'image',
                  'table', // Add table format
                ]}
                modules={{
                  toolbar: [
                    [
                      { header: '1' },
                      { header: '2' },
                      { font: fontFamilyArr },
                      { size: fontSizeArr },
                    ],
                    [{ list: 'ordered' }, { list: 'bullet' }],
                    ['bold', 'italic', 'underline', 'strike'],
                    [{ align: [] }],
                    ['link', 'image'],
                    ['clean'],
                  ],
                  clipboard: { matchVisual: false }, // Disable Quill's visual matching for pasting
                }}
              />
            </Box>
          </Box>
        </React.Fragment>
      </Box>

      {snackbarOpen && (
        <Snackbar
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
        >
          <Alert elevation={6} onClose={handleCloseSnackbar} severity={snackbarSeverity}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      )}
    </>
  )
}

export default EditablePdfReader
